import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSession } from '../../context/SessionContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { pushEstimateToJira as pushEstimateToJiraApi, api } from '../../utils/api';
import AsyncDeadlineBanner from './AsyncDeadlineBanner';
import AsyncProgressOverview from './AsyncProgressOverview';
import AsyncItemCard from './AsyncItemCard';
import ParticipantList from './ParticipantList';

const AsyncVotingView = ({ sessionId }) => {
  const { socket, session, participant, participants, items } = useSession();
  const { currentOrganization } = useWorkspace();
  const organizationId = currentOrganization?.id || null;

  const [asyncVotingStatus, setAsyncVotingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const [filterTab, setFilterTab] = useState(0);
  const [itemComments, setItemComments] = useState({});
  const [itemVotes, setItemVotes] = useState({});
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [jiraSettings, setJiraSettings] = useState(null);

  const isFacilitator = participant?.role === 'facilitator';
  const deckType = session?.cardDeckType || 'fibonacci';

  // Check if Jira is configured
  const jiraConfigured = !!(jiraSettings?.isJiraApiTokenSet && jiraSettings?.jiraUrl);

  // Fetch Jira settings for facilitator
  useEffect(() => {
    const fetchJiraSettings = async () => {
      if (!isFacilitator) return;
      try {
        const response = await api.get('/users/me/settings/jira');
        setJiraSettings(response.data);
      } catch (error) {
        console.error('Error fetching Jira settings:', error);
      }
    };
    fetchJiraSettings();
  }, [isFacilitator]);

  // Fetch async voting status
  const fetchVotingStatus = useCallback(() => {
    if (socket && socket.connected) {
      socket.emit('get-async-voting-status', { sessionId });
    }
  }, [socket, sessionId]);

  // Initial fetch and set up polling
  useEffect(() => {
    fetchVotingStatus();
    const interval = setInterval(fetchVotingStatus, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [fetchVotingStatus]);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleAsyncVotingStatus = (data) => {
      setAsyncVotingStatus(data);
      setLoading(false);

      // Populate itemVotes from status response for revealed/estimated items
      if (data.items) {
        const votesMap = {};
        data.items.forEach((item) => {
          if (item.votes && item.votes.length > 0) {
            votesMap[item.itemId] = item.votes;
          }
        });
        if (Object.keys(votesMap).length > 0) {
          setItemVotes((prev) => ({ ...prev, ...votesMap }));
        }
      }
    };

    const handleAsyncVotingProgress = (data) => {
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === data.itemId
              ? {
                  ...item,
                  votedCount: data.votedCount,
                  totalVoters: data.totalVoters,
                  isComplete: data.isComplete
                }
              : item
          )
        };
      });
    };

    const handleAsyncVoteRecorded = (data) => {
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === data.itemId
              ? { ...item, currentParticipantVote: data.value }
              : item
          )
        };
      });
    };

    const handleAsyncItemRevealed = (data) => {
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === data.itemId
              ? { ...item, status: data.itemStatus }
              : item
          )
        };
      });

      // Store the revealed votes
      setItemVotes((prev) => ({
        ...prev,
        [data.itemId]: data.votes
      }));
    };

    const handleAsyncAllItemsRevealed = () => {
      fetchVotingStatus();
    };

    const handleItemEstimated = (data) => {
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === data.itemId
              ? { ...item, status: 'estimated', finalEstimate: data.finalEstimate }
              : item
          )
        };
      });
    };

    const handleNextRoundStarted = (data) => {
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === data.itemId
              ? { ...item, status: 'active', votedCount: 0, isComplete: false }
              : item
          )
        };
      });
      // Clear votes for this item
      setItemVotes((prev) => ({
        ...prev,
        [data.itemId]: []
      }));
    };

    const handleCommentAdded = (data) => {
      setItemComments((prev) => ({
        ...prev,
        [data.itemId]: [...(prev[data.itemId] || []), data.comment]
      }));
    };

    const handleCommentDeleted = (data) => {
      setItemComments((prev) => ({
        ...prev,
        [data.itemId]: (prev[data.itemId] || []).filter(
          (c) => c.id !== data.commentId
        )
      }));
    };

    socket.on('async-voting-status', handleAsyncVotingStatus);
    socket.on('async-voting-progress', handleAsyncVotingProgress);
    socket.on('async-vote-recorded', handleAsyncVoteRecorded);
    socket.on('async-item-revealed', handleAsyncItemRevealed);
    socket.on('async-all-items-revealed', handleAsyncAllItemsRevealed);
    socket.on('comment-added', handleCommentAdded);
    socket.on('comment-deleted', handleCommentDeleted);
    socket.on('item-estimated', handleItemEstimated);
    socket.on('next-round-started', handleNextRoundStarted);

    return () => {
      socket.off('async-voting-status', handleAsyncVotingStatus);
      socket.off('async-voting-progress', handleAsyncVotingProgress);
      socket.off('async-vote-recorded', handleAsyncVoteRecorded);
      socket.off('async-item-revealed', handleAsyncItemRevealed);
      socket.off('async-all-items-revealed', handleAsyncAllItemsRevealed);
      socket.off('comment-added', handleCommentAdded);
      socket.off('comment-deleted', handleCommentDeleted);
      socket.off('item-estimated', handleItemEstimated);
      socket.off('next-round-started', handleNextRoundStarted);
    };
  }, [socket, fetchVotingStatus]);

  // Handle voting
  const handleVote = (itemId, value) => {
    if (socket && socket.connected) {
      socket.emit('async-vote', { itemId, value });
    }
  };

  // Handle reveal single item
  const handleRevealItem = (itemId) => {
    if (socket && socket.connected && isFacilitator) {
      socket.emit('async-reveal-item', { itemId });
    }
  };

  // Handle reveal all items
  const handleRevealAll = () => {
    if (socket && socket.connected && isFacilitator) {
      socket.emit('async-reveal-all', { sessionId });
    }
  };

  // Handle deadline expiration
  const handleDeadlineExpired = () => {
    setIsDeadlinePassed(true);
  };

  // Handle set final estimate
  const handleSetFinalEstimate = (itemId, estimate) => {
    if (socket && socket.connected && isFacilitator) {
      socket.emit('set-final-estimate', { itemId, finalEstimate: estimate });
      // Update local state optimistically
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === itemId
              ? { ...item, status: 'estimated', finalEstimate: estimate }
              : item
          )
        };
      });
    }
  };

  // Handle start new round
  const handleStartNewRound = (itemId) => {
    if (socket && socket.connected && isFacilitator) {
      socket.emit('start-next-round', { itemId });
      // Update local state
      setAsyncVotingStatus((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.itemId === itemId
              ? { ...item, status: 'active', votedCount: 0, isComplete: false }
              : item
          )
        };
      });
      // Clear votes for this item
      setItemVotes((prev) => ({
        ...prev,
        [itemId]: []
      }));
    }
  };

  // Handle push to Jira
  const handlePushToJira = async (itemId, jiraKey, estimate) => {
    if (!jiraConfigured || !jiraSettings) {
      throw new Error('Jira not configured');
    }

    await pushEstimateToJiraApi(jiraKey, {
      jiraUrl: jiraSettings.jiraUrl,
      username: jiraSettings.jiraUsername,
      storyPointFieldId: jiraSettings.jiraStoryPointFieldId,
      estimate: estimate,
      organizationId: organizationId,
    });
  };


  // Filter items based on tab
  const getFilteredItems = () => {
    if (!asyncVotingStatus?.items) return [];

    const itemsWithData = asyncVotingStatus.items.map((statusItem) => {
      const fullItem = items.find((i) => i.id === statusItem.itemId) || {
        id: statusItem.itemId,
        title: statusItem.title,
        status: statusItem.status
      };
      return {
        ...fullItem,
        ...statusItem
      };
    });

    switch (filterTab) {
      case 1: // Pending
        return itemsWithData.filter(
          (item) =>
            item.status !== 'estimated' &&
            item.status !== 'revealed' &&
            !item.isComplete
        );
      case 2: // Ready to Reveal
        return itemsWithData.filter(
          (item) =>
            item.status !== 'estimated' &&
            item.status !== 'revealed' &&
            item.isComplete
        );
      case 3: // Revealed/Estimated
        return itemsWithData.filter(
          (item) => item.status === 'estimated' || item.status === 'revealed'
        );
      default: // All
        return itemsWithData;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const filteredItems = getFilteredItems();
  const totalVoters =
    asyncVotingStatus?.items?.[0]?.totalVoters ||
    participants.filter((p) => p.isVoting !== false).length;

  // Count items ready to reveal (for facilitator notification)
  const readyToRevealCount =
    asyncVotingStatus?.items?.filter(
      (item) =>
        item.status !== 'estimated' &&
        item.status !== 'revealed' &&
        item.isComplete
    ).length || 0;

  return (
    <Box>
      {/* Deadline Banner */}
      {session?.asyncDeadline && (
        <AsyncDeadlineBanner
          deadline={session.asyncDeadline}
          onDeadlineExpired={handleDeadlineExpired}
        />
      )}

      <Grid container spacing={3}>
        {/* Left Column - Participants & Progress */}
        <Grid item xs={12} md={4}>
          <AsyncProgressOverview
            items={asyncVotingStatus?.items || []}
            totalVoters={totalVoters}
          />

          <ParticipantList
            participants={participants}
            isFacilitator={isFacilitator}
            currentParticipantId={participant?.id}
            sessionId={sessionId}
          />

          {/* Facilitator Controls */}
          {isFacilitator && readyToRevealCount > 0 && (
            <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Facilitator Actions
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                {readyToRevealCount} item{readyToRevealCount !== 1 ? 's' : ''}{' '}
                ready to reveal
              </Alert>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<VisibilityIcon />}
                onClick={handleRevealAll}
              >
                Reveal All Ready Items
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Items */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6">Items to Estimate</Typography>
              <FilterListIcon color="action" />
            </Box>

            <Tabs
              value={filterTab}
              onChange={(e, v) => setFilterTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`All (${asyncVotingStatus?.items?.length || 0})`} />
              <Tab
                label={`Pending (${
                  asyncVotingStatus?.items?.filter(
                    (i) =>
                      i.status !== 'estimated' &&
                      i.status !== 'revealed' &&
                      !i.isComplete
                  ).length || 0
                })`}
              />
              <Tab label={`Ready (${readyToRevealCount})`} />
              <Tab
                label={`Complete (${
                  asyncVotingStatus?.items?.filter(
                    (i) => i.status === 'estimated' || i.status === 'revealed'
                  ).length || 0
                })`}
              />
            </Tabs>
          </Paper>

          {filteredItems.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No items match the current filter.
              </Typography>
            </Paper>
          ) : (
            <Box>
              {filteredItems.map((item) => (
                <AsyncItemCard
                  key={item.itemId || item.id}
                  item={{
                    id: item.itemId || item.id,
                    title: item.title,
                    description: item.description,
                    externalId: item.externalId,
                    status: item.status,
                    finalEstimate: item.finalEstimate
                  }}
                  votedCount={item.votedCount}
                  totalVoters={item.totalVoters}
                  currentParticipantVote={item.currentParticipantVote}
                  deckType={deckType}
                  onVote={handleVote}
                  onReveal={handleRevealItem}
                  onSetFinalEstimate={handleSetFinalEstimate}
                  onStartNewRound={handleStartNewRound}
                  onPushToJira={handlePushToJira}
                  isFacilitator={isFacilitator}
                  isDeadlinePassed={isDeadlinePassed}
                  comments={itemComments[item.itemId || item.id] || []}
                  votes={itemVotes[item.itemId || item.id] || []}
                  jiraKey={item.jiraKey || item.externalId}
                  jiraUrl={item.jiraUrl}
                  jiraConfigured={jiraConfigured}
                  participants={participants}
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AsyncVotingView;
